import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShaileshButton } from '../components/Shailesh';
import { ageCheck, allocateTax, cashRoundingAdjustment, cashRoundingAllowed, parseAamva, stateRules, todayIn } from '@njpos/core';
import { api, ApiError, NetworkError, saveSession, sizeLabel, usd, type Session } from '../lib/api';
import { cachedItems, enqueue, findCachedByCode, flushQueue, problems, queued, refreshItemCache, startQueueLoop, type CachedItem } from '../lib/offline';
import { ErrorNote, Icon, Modal } from '../components/ui';
import MasterSearch from '../components/MasterSearch';
import { DrawerModal } from '../components/Drawer';
import { CouponPrompt, PickupOrders, RecentReceipts, SplitModal } from '../components/SalesTools';
import { printReceipt } from '../lib/receipt';
import { kickForSale } from '../lib/drawer';
import DrawerSetup from '../components/DrawerSetup';
import { KEY_COLORS } from './RegisterLayout';
import { useAccess } from '../lib/access';
import { CardTerminalModal, type CardResult } from '../components/CardTerminal';
import { CustomerPicker, GiftCardLookup, LotteryQuick, RecallCart } from '../components/RegisterTools';

interface Line { item: CachedItem; qty: number; discountCents: number }
interface StoreInfo { id: string; name: string; tax_rate_ppm: number; cash_rounding: boolean; timezone: string; state_code?: string }
type AgeResult = { method: 'id_scan' | 'visual'; passed: boolean; idExpired?: boolean };

const NON_ALCOHOL = new Set(['non_alcoholic', 'accessory']);
const ME_CACHE = 'njpos.me';

export default function Register({ session }: { session: Session }) {
  const access = useAccess();
  const [store, setStore] = useState<StoreInfo | null>(() => { try { return JSON.parse(localStorage.getItem(ME_CACHE) ?? 'null')?.store ?? null; } catch { return null; } });
  const [registerId, setRegisterId] = useState<string | undefined>();
  const [rawLines, setLines] = useState<Line[]>([]);
  const [sel, setSel] = useState(-1);
  const [code, setCode] = useState('');
  const [online, setOnline] = useState(navigator.onLine);
  const [queueCount, setQueueCount] = useState(queued().length);
  const [problemCount, setProblemCount] = useState(problems().length);
  const [error, setError] = useState<unknown>(null);
  const [modal, setModal] = useState<null | { kind: 'catalog'; product: any; code: string } | { kind: 'new'; code: string } | { kind: 'master'; q: string } | { kind: 'customer' } | { kind: 'gift' } | { kind: 'recall' } | { kind: 'lottery' } | { kind: 'drawer' } | { kind: 'split' } | { kind: 'coupon' } | { kind: 'orders' } | { kind: 'receipts' } | { kind: 'held'; code: string } | { kind: 'pay'; method: 'cash' | 'card' } | { kind: 'age'; method: 'cash' | 'card' | 'gift' | 'split' | 'account'; cashCents?: number } | { kind: 'done'; total: number; change: number; offline: boolean; saleId?: string }>(null);
  const [matches, setMatches] = useState<CachedItem[]>([]);
  const scanRef = useRef<HTMLInputElement>(null);
  const [clockedIn, setClockedIn] = useState<boolean | null>(null);
  useEffect(() => { api<{ open: unknown }>('/api/timeclock/status').then((r) => setClockedIn(!!r.open)).catch(() => setClockedIn(null)); }, [session.storeId]);
  async function toggleClock() {
    try { await api(`/api/timeclock/${clockedIn ? 'out' : 'in'}`, { body: {} }); setClockedIn(!clockedIn); } catch (e) { setError(e); }
  }

  // Loyalty customer, points, gift card and the server's promotion quote for this cart.
  const [customer, setCustomer] = useState<any>(null);
  const [redeem, setRedeem] = useState(0);
  const [gift, setGift] = useState<{ code: string; balance: number } | null>(null);
  const [couponCodes, setCouponCodes] = useState<string[]>([]);
  const [pickup, setPickup] = useState<{ orderId: string; depositLeft: number } | null>(null);
  const [pendingTenders, setPendingTenders] = useState<any[] | null>(null);
  const [quote, setQuote] = useState<any>(null);
  const [quoteErr, setQuoteErr] = useState<string | null>(null);
  const [quoteNet, setQuoteNet] = useState(false); // the quote request could not reach the server: sell at shelf price
  const quoteKey = JSON.stringify([rawLines.map((l) => [l.item.id, l.qty]), customer?.id ?? null, redeem, couponCodes]);
  useEffect(() => {
    if (!online || rawLines.length === 0) { setQuote(null); setQuoteErr(null); setQuoteNet(false); return; }
    const key = quoteKey;
    const t = setTimeout(() => {
      api<any>('/api/sales/quote', { body: { lines: rawLines.map((l) => ({ itemId: l.item.id, qty: l.qty })), customerId: customer?.id ?? null, redeemPoints: redeem, couponCodes } })
        .then((q) => { setQuote({ ...q, key }); setQuoteErr(null); setQuoteNet(false); })
        .catch((e) => { setQuote(null); if (e instanceof NetworkError) { setQuoteNet(true); setQuoteErr(null); } else { setQuoteNet(false); setQuoteErr(e instanceof Error ? e.message : 'Could not total this sale'); } });
    }, 150);
    return () => clearTimeout(t);
  }, [quoteKey, online]); // eslint-disable-line react-hooks/exhaustive-deps
  const quoteFresh = quote?.key === quoteKey;
  const quoted = online && rawLines.length > 0 && quoteFresh;
  const waitingForQuote = online && rawLines.length > 0 && !quoteFresh && !quoteNet; // totals not settled yet, or the quote was refused
  const lines = useMemo(() => rawLines.map((l, i) => ({ ...l, discountCents: quoted ? (quote.lineDiscounts[i] ?? 0) : 0 })), [rawLines, quote, quoted]);
  const discountTotal = lines.reduce((s, l) => s + l.discountCents, 0);
  const resetSale = () => { setLines([]); setSel(-1); setCustomer(null); setRedeem(0); setGift(null); setCouponCodes([]); setPickup(null); setPendingTenders(null); };

  const focusScan = useCallback(() => setTimeout(() => scanRef.current?.focus(), 0), []);

  useEffect(() => {
    api<any>('/api/me').then((me) => {
      const s = me.stores.find((x: any) => x.id === session.storeId);
      setStore(s);
      setRegisterId(me.registers.find((r: any) => r.store_id === session.storeId)?.id);
      localStorage.setItem(ME_CACHE, JSON.stringify({ store: s }));
    }).catch(() => {});
    refreshItemCache().catch(() => {});
    const loop = startQueueLoop();
    const onQueue = () => { setQueueCount(queued().length); setProblemCount(problems().length); };
    const on = () => setOnline(true), off = () => setOnline(false);
    window.addEventListener('njpos:queue', onQueue);
    window.addEventListener('online', on); window.addEventListener('offline', off);
    return () => { clearInterval(loop); window.removeEventListener('njpos:queue', onQueue); window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, [session.storeId]);

  useEffect(() => { if (!modal) focusScan(); }, [modal, focusScan]);

  // Quick keys set under Register buttons. The last layout is kept so the keys still show offline.
  const [quickKeys, setQuickKeys] = useState<any[]>(() => { try { return JSON.parse(localStorage.getItem(`njpos.keys.${session.storeId}`) ?? '[]'); } catch { return []; } });
  useEffect(() => {
    api<{ keys: any[] }>('/api/register-layout').then((r) => { setQuickKeys(r.keys); try { localStorage.setItem(`njpos.keys.${session.storeId}`, JSON.stringify(r.keys)); } catch { /* storage full or blocked */ } }).catch(() => {});
  }, [session.storeId]);
  const tapKey = (k: any) => {
    const item = cachedItems().find((i) => i.id === k.itemId);
    if (item) { add(item); focusScan(); } else setError(new Error(`${k.label} is not in the local item list yet. Scan it once, or wait for the list to refresh.`));
  };

  const add = (item: CachedItem, qty = 1) => {
    setLines((ls) => {
      const i = ls.findIndex((l) => l.item.id === item.id);
      if (i >= 0) { const copy = [...ls]; copy[i] = { ...copy[i]!, qty: copy[i]!.qty + qty }; setSel(i); return copy; }
      setSel(ls.length);
      return [...ls, { item, qty, discountCents: 0 }];
    });
  };

  async function onScan(e: React.FormEvent) {
    e.preventDefault();
    const raw = code.trim();
    setCode(''); setError(null); setMatches([]);
    if (!raw) return;
    if (!/^\d{6,14}$/.test(raw.replace(/[\s-]/g, ''))) {
      const q = raw.toLowerCase();
      const found = cachedItems().filter((i) => `${i.brand} ${i.name}`.toLowerCase().includes(q)).slice(0, 8);
      if (found.length === 1) add(found[0]!); else if (found.length) setMatches(found);
      else if (online) setModal({ kind: 'master', q: raw }); // not in this store: search the shared master list
      else setError(new Error(`No item matches “${raw}”`));
      return;
    }
    const local = findCachedByCode(raw);
    if (local) return add(local);
    try {
      const r = await api<any>(`/api/scan/${encodeURIComponent(raw)}`);
      if (r.source === 'store') {
        const item: CachedItem = { ...r.item, brand: r.product.brand, name: r.product.name, size_ml: r.product.size_ml, category: r.product.category, codes: [r.code] };
        add(item, r.qtyPer ?? 1);
        refreshItemCache().catch(() => {});
      } else if (r.source === 'catalog') setModal({ kind: 'catalog', product: r.product, code: r.code });
      else setModal({ kind: 'new', code: raw });
    } catch (err) {
      setError(err instanceof NetworkError ? new Error('Offline and this code is not in the local item list. Ring it up by name or wait for the connection.') : err);
    }
  }

  const tax = useMemo(() => {
    const nets = lines.map((l) => l.item.retail_cents * l.qty - l.discountCents);
    const per = allocateTax(nets, store?.tax_rate_ppm ?? 66250);
    return { subtotal: nets.reduce((a, b) => a + b, 0), tax: per.reduce((a, b) => a + b, 0) };
  }, [lines, store]);
  const total = tax.subtotal + tax.tax;
  const roundCash = !!store?.cash_rounding && cashRoundingAllowed(stateRules(store.state_code), todayIn(store.timezone));
  const giftApplied = gift ? Math.min(gift.balance, total) : 0;
  const depositApplied = pickup ? Math.min(pickup.depositLeft, Math.max(0, total - giftApplied)) : 0;
  const due = total - giftApplied - depositApplied;   // what is left to take by cash, card or account
  const extraTenders = [
    ...(giftApplied > 0 ? [{ method: 'gift', amountCents: giftApplied, reference: gift!.code }] : []),
    ...(depositApplied > 0 ? [{ method: 'deposit', amountCents: depositApplied, reference: pickup!.orderId }] : []),
  ];
  const needsAge = lines.some((l) => !NON_ALCOHOL.has(l.item.category));

  // Card terminal (set up under Card terminal in the back office). When it is on, a card payment goes to the reader first and the sale only saves if it is approved.
  const [terminal, setTerminal] = useState<{ enabled: boolean; live?: boolean; readerId?: string | null } | null>(null);
  useEffect(() => { if (online) api<any>('/api/card/status').then(setTerminal).catch(() => {}); }, [session.storeId, online]);
  const [cardAsk, setCardAsk] = useState<null | { amountCents: number; attemptId: string; resolve: (r: CardResult | null, m?: string) => void }>(null);
  const takeCard = (amountCents: number, attemptId: string) =>
    new Promise<{ r: CardResult | null; m?: string }>((resolve) => setCardAsk({ amountCents, attemptId, resolve: (r, m) => { setCardAsk(null); resolve({ r, m }); } }));
  const refundCharged = async (ids: string[]) => {
    const results = await Promise.allSettled(ids.map((id) => api(`/api/card/payments/${id}/void-unused`, { body: {} })));
    return results.every((x) => x.status === 'fulfilled');
  };

  const [drawerSetup, setDrawerSetup] = useState(false);

  async function submitSale(tendersIn: any[], age: AgeResult | null, offline: { total: number; change: number }) {
    const clientTxnId = crypto.randomUUID();
    kickForSale(tendersIn);   // cash is being taken: pop the drawer
    let tenders = tendersIn;
    const charged: string[] = [];
    if (terminal?.enabled && tenders.some((t) => t.method === 'card' && t.amountCents > 0)) {
      setModal(null);
      if (!online) { setError(new Error('The card terminal needs a connection. Take cash, or wait for the connection.')); return; }
      const out: any[] = [];
      for (const [i, t] of tenders.entries()) {
        if (t.method !== 'card' || t.amountCents <= 0) { out.push(t); continue; }
        const { r, m } = await takeCard(t.amountCents, `${clientTxnId}-${i}`);
        if (!r) {
          const ok = await refundCharged(charged);
          setError(new Error(`${m ?? 'The card payment did not go through.'}${charged.length ? (ok ? ' The part already charged was refunded.' : ' A card payment was already taken: refund it under Card terminal.') : ''}`));
          return;
        }
        charged.push(r.id); out.push({ ...t, reference: r.reference });
      }
      tenders = out;
    }
    const sale = {
      clientTxnId,
      registerId,
      soldAt: new Date().toISOString(),
      lines: lines.map((l) => ({ itemId: l.item.id, qty: l.qty, ...(l.discountCents ? { discountCents: l.discountCents } : {}) })),
      tenders,
      ageCheck: needsAge ? age : null,
      ...(customer ? { customerId: customer.id } : {}),
      ...(redeem > 0 ? { redeemPoints: redeem } : {}),
      ...(quoted ? { quoted: true } : {}),
      ...(quoted && couponCodes.length ? { couponCodes } : {}),
      ...(pickup ? { specialOrderId: pickup.orderId } : {}),
    };
    // Gift cards, points, coupons, deposits and house accounts are checked by the server, so they need a connection.
    const onlineOnly = !!(gift || redeem > 0 || couponCodes.length || pickup || tenders.some((t) => t.method === 'house_account' || t.method === 'gift' || t.method === 'deposit'));
    try {
      const r = await api<any>('/api/sales', { body: sale });
      setModal({ kind: 'done', total: r.sale.total_cents, change: r.sale.change_cents ?? 0, offline: false, saleId: r.sale.id });
      resetSale();
    } catch (err) {
      if (err instanceof NetworkError && onlineOnly) {
        setModal(null);
        setError(new Error('No connection. Gift cards, points, coupons, deposits and house accounts need to be online. Take cash or card, or wait for the connection.'));
      } else if (err instanceof NetworkError) {
        enqueue(sale);
        setModal({ kind: 'done', total: offline.total, change: offline.change, offline: true });
        resetSale();
      } else {
        setModal(null);
        if (charged.length) {
          const ok = await refundCharged(charged);
          setError(new Error(`${err instanceof Error ? err.message : 'The sale could not be saved.'} ${ok ? 'The card payment was refunded.' : 'The card was charged: refund it under Card terminal.'}`));
        } else setError(err);
      }
    }
  }

  async function complete(method: 'cash' | 'card' | 'gift', cashCents: number | undefined, age: AgeResult | null) {
    const rounding = method === 'cash' && !gift && !pickup && roundCash ? cashRoundingAdjustment(total) : 0;
    const owed = due + rounding;
    const tenders = [
      ...extraTenders,
      ...(owed > 0 ? [{ method: method === 'gift' ? ('card' as const) : method, amountCents: method === 'cash' ? (cashCents ?? owed) : owed }] : []),
    ];
    return submitSale(tenders, age, { total: total + rounding, change: method === 'cash' ? (cashCents ?? owed) - owed : 0 });
  }

  /** Split payment or charge to account: the tenders are chosen first, then the ID check, then the sale. */
  function payWith(chosen: any[]) {
    const all = [...extraTenders, ...chosen];
    const paid = all.reduce((sum, t) => sum + t.amountCents, 0);
    setPendingTenders(all);
    if (needsAge) setModal({ kind: 'age', method: 'split' });
    else { setModal(null); submitSale(all, null, { total, change: Math.max(0, paid - total) }); }
  }

  /** Load a special order into the cart with its customer and deposit. */
  async function loadOrder(order: any) {
    let known = cachedItems();
    if (order.lines.some((l: any) => l.item_id && !known.some((i) => i.id === l.item_id))) { await refreshItemCache().catch(() => {}); known = cachedItems(); }
    const next: Line[] = []; const missing: string[] = [];
    for (const l of order.lines) {
      const item = l.item_id ? known.find((i) => i.id === l.item_id) : null;
      if (item) next.push({ item, qty: l.qty, discountCents: 0 }); else missing.push(l.description);
    }
    let cust: any = null;
    if (order.customer_id) cust = await api(`/api/customers/${order.customer_id}`).catch(() => null);
    setLines(next); setSel(next.length ? 0 : -1); setCustomer(cust); setRedeem(0); setGift(null); setCouponCodes([]);
    setPickup({ orderId: order.id, depositLeft: order.depositLeftCents });
    setModal(null);
    if (missing.length) setError(new Error(`These lines are not items in your store yet, so they were left off: ${missing.join(', ')}. Add them from the master list and ring them up by hand.`));
  }

  // Put the cart on hold for someone to finish later, or pick up a cart built on a phone.
  async function holdSale() {
    setError(null);
    try {
      const r = await api<any>('/api/held-carts', { body: { lines: rawLines.map((l) => ({ itemId: l.item.id, qty: l.qty })), customerId: customer?.id ?? null } });
      resetSale(); setModal({ kind: 'held', code: r.code });
    } catch (e) { setError(e); }
  }
  async function loadCart(cart: { lines: { itemId: string; qty: number }[]; customer: any | null }) {
    let known = cachedItems();
    if (cart.lines.some((l) => !known.some((i) => i.id === l.itemId))) { await refreshItemCache().catch(() => {}); known = cachedItems(); }
    const next: Line[] = [];
    for (const l of cart.lines) { const item = known.find((i) => i.id === l.itemId); if (item) next.push({ item, qty: l.qty, discountCents: 0 }); }
    setLines(next); setSel(next.length ? 0 : -1); setCustomer(cart.customer); setRedeem(0); setModal(null);
    if (next.length < cart.lines.length) setError(new Error('Some items on that cart are no longer in the store.'));
  }

  const keyHandler = (e: React.KeyboardEvent) => {
    if (modal || sel < 0) return;
    if (e.key === '+' ) { e.preventDefault(); setLines((ls) => ls.map((l, i) => (i === sel ? { ...l, qty: l.qty + 1 } : l))); }
    if (e.key === '-' ) { e.preventDefault(); setLines((ls) => ls.map((l, i) => (i === sel ? { ...l, qty: Math.max(1, l.qty - 1) } : l))); }
    if (e.key === 'Delete') { e.preventDefault(); setLines((ls) => ls.filter((_, i) => i !== sel)); setSel(-1); }
  };

  return (
    <div className="register" onKeyDown={keyHandler}>
      <div className="reg-top">
        <span className="brand">{store?.name ?? 'Register'}</span>
        <span className="grow faint" style={{ color: '#9fb4a7' }}>{session.user.name}</span>
        <span className={`status ${online ? '' : 'off'}`}>{online ? 'Online' : 'Offline — sales are saved here'}{queueCount ? ` · ${queueCount} to send` : ''}</span>
        {problemCount > 0 && <span className="status off" title="Sales the server refused after reconnecting">{problemCount} need a manager</span>}
        {queueCount > 0 && online && <button onClick={() => flushQueue()}>Send now</button>}
        {online && clockedIn !== null && <button onClick={toggleClock}>{clockedIn ? 'Clock out' : 'Clock in'}</button>}
        {online && <button onClick={() => setModal({ kind: 'drawer' })}><Icon name="cash" size={16} />Drawer</button>}
        <button onClick={() => setDrawerSetup(true)} title="How the cash drawer opens on this computer">Drawer pop</button>
        {online && <ShaileshButton page="/register" inline />}
        {session.user.role !== 'cashier' && <Link to="/" className="btn" style={{ background: 'transparent', color: '#dfe8e2', borderColor: 'rgba(255,255,255,.2)', minHeight: 32 }}>Back office</Link>}
        <button onClick={() => saveSession(null)}><Icon name="logout" size={16} />Sign out</button>
      </div>
      <div className="reg-body">
        <div className="cart">
          <div className="stack-s">
            <form className="scanbar" onSubmit={onScan}>
              <input ref={scanRef} autoFocus value={code} onChange={(e) => setCode(e.target.value)} placeholder="Scan a barcode or type a name" aria-label="Scan or search" />
              <button className="primary big"><Icon name="plus" />Add</button>
            </form>
            <ErrorNote error={error} />
            {matches.length > 0 && (
              <div className="panel">
                {matches.map((m) => (
                  <button key={m.id} className="ghost" style={{ width: '100%', justifyContent: 'space-between', borderRadius: 0 }} onClick={() => { add(m); setMatches([]); focusScan(); }}>
                    <span>{m.brand} {m.name} · {sizeLabel(m.size_ml)}</span><span className="num">{usd(m.retail_cents)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {quickKeys.length > 0 && (
            <div className="row wrap" aria-label="Quick keys" style={{ gap: 6, padding: '6px 0' }}>
              {quickKeys.map((k) => (
                <button key={k.itemId} style={{ background: KEY_COLORS[k.color ?? 'gray'], color: '#111', minWidth: 96, minHeight: 52, whiteSpace: 'normal' }} onClick={() => tapKey(k)}>
                  <span style={{ display: 'block', fontWeight: 600, lineHeight: 1.15 }}>{k.label}</span><span className="num" style={{ fontSize: 12 }}>{usd(k.retail_cents)}</span>
                </button>))}
            </div>
          )}
          <div className="cart-lines" aria-label="Cart">
            {lines.length === 0 && <div className="empty">Scan the first item to start a sale.</div>}
            {lines.map((l, i) => (
              <div key={l.item.id} className={`cart-line${i === sel ? ' selected' : ''}`} onClick={() => setSel(i)}>
                <div>
                  <div className="title">{l.item.brand} {l.item.name}</div>
                  <div className="meta">{sizeLabel(l.item.size_ml)} · {usd(l.item.retail_cents)} each{l.item.on_hand <= 0 ? ' · not in stock records' : ''}</div>
                </div>
                <div className="qty">
                  <button aria-label="One less" onClick={(e) => { e.stopPropagation(); setLines((ls) => ls.map((x, j) => (j === i ? { ...x, qty: Math.max(1, x.qty - 1) } : x))); }}><Icon name="minus" size={16} /></button>
                  <span>{l.qty}</span>
                  <button aria-label="One more" onClick={(e) => { e.stopPropagation(); setLines((ls) => ls.map((x, j) => (j === i ? { ...x, qty: x.qty + 1 } : x))); }}><Icon name="plus" size={16} /></button>
                </div>
                <div className="price">{usd(l.item.retail_cents * l.qty - l.discountCents)}</div>
                <button className="ghost danger" aria-label="Remove" onClick={(e) => { e.stopPropagation(); setLines((ls) => ls.filter((_, j) => j !== i)); setSel(-1); }}><Icon name="x" size={16} /></button>
              </div>
            ))}
          </div>
        </div>
        <aside className="side">
          <div className="totals">
            <div className="line"><span>Items</span><span>{lines.reduce((s, l) => s + l.qty, 0)}</span></div>
            {discountTotal > 0 && <div className="line"><span>Discounts</span><span>−{usd(discountTotal)}</span></div>}
            <div className="line"><span>Subtotal</span><span>{usd(tax.subtotal)}</span></div>
            <div className="line"><span>NJ sales tax</span><span>{usd(tax.tax)}</span></div>
            <div className="grand"><span>Total</span><span>{usd(total)}</span></div>
            {gift && <div className="line"><span>Gift card {gift.code}</span><span>−{usd(giftApplied)}</span></div>}
            {depositApplied > 0 && <div className="line"><span>Deposit on order</span><span>−{usd(depositApplied)}</span></div>}
            {quoted && quote.group && <div className="faint">Group pricing: {quote.group.name}{quote.group.discount_pct ? ` (${quote.group.discount_pct}% off)` : ''}</div>}
            {quoted && quote.deals?.map((d: any) => <div key={d.id} className="faint">Deal: {d.name}</div>)}
            {quoted && quote.capped?.length > 0 && <div className="faint">A deal is limited here so a bottle does not sell below its legal cost.</div>}
            {quote?.coupons?.applied?.map((c: any) => <div key={c.id} className="faint">Coupon {c.code}: {c.name}</div>)}
            {couponCodes.length > 0 && <div className="row wrap">{couponCodes.map((c) => <button key={c} className="ghost" onClick={() => setCouponCodes(couponCodes.filter((x) => x !== c))}>{c} ✕</button>)}</div>}
            {quote?.coupons?.errors?.map((e: any) => <div key={e.code} className="notice bad">{e.code}: {e.message}</div>)}
            {quoted && quote.pointsEarned > 0 && <p className="faint">{customer?.name} earns {quote.pointsEarned} points.</p>}
            {needsAge && lines.length > 0 && <p className="faint">ID check required before payment.</p>}
          </div>
          <div className="pay">
            <ErrorNote error={quoteErr ? new Error(quoteErr) : null} />
            {online && (
              <div className="two">
                <button onClick={() => setModal({ kind: 'customer' })}><Icon name="users" />{customer ? `${customer.name} · ${customer.points} pts` : 'Customer'}</button>
                <button onClick={() => setModal({ kind: 'gift' })}><Icon name="gift" />{gift ? `Gift card ${usd(gift.balance)}` : 'Gift card'}</button>
              </div>
            )}
            {online && (
              <>
              <div className="two">
                <button disabled={!rawLines.length} onClick={holdSale}><Icon name="hold" />Hold</button>
                <button onClick={() => setModal({ kind: 'recall' })}><Icon name="recall" />Recall</button>
                {access.has('lottery') && <button onClick={() => setModal({ kind: 'lottery' })}>Lottery</button>}
              </div>
              <div className="two">
                <button disabled={!lines.length || waitingForQuote || due <= 0} onClick={() => setModal({ kind: 'split' })}><Icon name="split" />Split payment</button>
                <button disabled={!rawLines.length} onClick={() => setModal({ kind: 'coupon' })}><Icon name="percent" />Coupon</button>
                {customer?.account_enabled && <button disabled={!lines.length || waitingForQuote || due <= 0} onClick={() => payWith([{ method: 'house_account', amountCents: due }])}>Charge to account</button>}
                <button onClick={() => setModal({ kind: 'orders' })}>Special order</button>
                <button onClick={() => setModal({ kind: 'receipts' })}><Icon name="receipt" />Receipts</button>
              </div>
              </>
            )}
            {customer && customer.points >= 100 && (
              <label>Use points<select value={redeem} onChange={(e) => setRedeem(Number(e.target.value))}>
                <option value={0}>None</option>
                {Array.from({ length: Math.floor(customer.points / 100) }, (_, n) => (n + 1) * 100).slice(0, 20).map((p) => <option key={p} value={p}>{p} points · {usd((p / 100) * 500)} off</option>)}
              </select></label>
            )}
            <div className="two">
              {total > 0 && giftApplied + depositApplied >= total
                ? <button className="primary big" disabled={!lines.length || waitingForQuote} onClick={() => (needsAge ? setModal({ kind: 'age', method: 'gift' }) : complete('gift', undefined, null))}>{gift ? 'Pay with gift card' : 'Apply deposit'}</button>
                : <>
                    <button className="big pay-cash" disabled={!lines.length || waitingForQuote} onClick={() => setModal({ kind: 'pay', method: 'cash' })}><Icon name="cash" size={26} />Cash</button>
                    <button className="primary big pay-card" disabled={!lines.length || waitingForQuote} onClick={() => (needsAge ? setModal({ kind: 'age', method: 'card' }) : complete('card', undefined, null))}><Icon name="card" size={26} />Card</button>
                  </>}
            </div>
            <button className="ghost" disabled={!lines.length && !customer && !gift} onClick={resetSale}><Icon name="trash" />Clear sale</button>
          </div>
        </aside>
      </div>

      {modal?.kind === 'pay' && (
        <CashModal total={due} rounding={roundCash && !gift && !pickup ? cashRoundingAdjustment(total) : 0} onClose={() => setModal(null)}
          onTender={(cents) => (needsAge ? setModal({ kind: 'age', method: 'cash', cashCents: cents }) : complete('cash', cents, null))} />
      )}
      {modal?.kind === 'age' && (
        <AgeModal timezone={store?.timezone} onClose={() => setModal(null)} onPass={(age) => (modal.method === 'split' || modal.method === 'account' ? submitSale(pendingTenders ?? [], age, { total, change: Math.max(0, (pendingTenders ?? []).reduce((sum: number, t: any) => sum + t.amountCents, 0) - total) }) : complete(modal.method, modal.cashCents, age))} />
      )}
      {modal?.kind === 'catalog' && (
        <AddFromCatalog product={modal.product} onClose={() => setModal(null)} onAdded={(item) => { setModal(null); add(item); refreshItemCache().catch(() => {}); }} />
      )}
      {modal?.kind === 'drawer' && <DrawerModal onClose={() => setModal(null)} />}
      {drawerSetup && <DrawerSetup onClose={() => setDrawerSetup(false)} canNoSale={session.user.role !== 'cashier'} />}
      {modal?.kind === 'split' && <SplitModal due={due} canCharge={!!customer?.account_enabled} customerName={customer?.name} onClose={() => setModal(null)} onConfirm={(t) => payWith(t)} />}
      {modal?.kind === 'coupon' && <CouponPrompt onClose={() => setModal(null)} onAdd={(code) => { setCouponCodes((c) => (c.includes(code) ? c : [...c, code])); setModal(null); }} />}
      {modal?.kind === 'orders' && <PickupOrders onClose={() => setModal(null)} onPick={loadOrder} />}
      {modal?.kind === 'receipts' && <RecentReceipts onClose={() => setModal(null)} />}
      {cardAsk && <CardTerminalModal amountCents={cardAsk.amountCents} attemptId={cardAsk.attemptId} readerId={terminal?.readerId ?? null} testMode={terminal?.live === false} onDone={cardAsk.resolve} />}
      {modal?.kind === 'lottery' && <LotteryQuick onClose={() => setModal(null)} />}
      {modal?.kind === 'recall' && <RecallCart onClose={() => setModal(null)} onRecall={loadCart} />}
      {modal?.kind === 'held' && (
        <Modal title="Cart on hold" onClose={() => setModal(null)} footer={<button className="primary big" onClick={() => setModal(null)}>Done</button>}>
          <p>Give the customer this number. Recall it from any lane.</p>
          <div style={{ fontSize: 56, fontWeight: 800, textAlign: 'center', letterSpacing: 4 }}>{modal.code}</div>
        </Modal>
      )}
      {modal?.kind === 'customer' && (
        <CustomerPicker onClose={() => setModal(null)} onPick={(c) => { setCustomer(c); setRedeem(0); setModal(null); }} />
      )}
      {modal?.kind === 'gift' && (
        <GiftCardLookup onClose={() => setModal(null)} onApply={(c) => { setGift(c); setModal(null); }} />
      )}
      {modal?.kind === 'master' && (
        <MasterSearch initialQuery={modal.q} onClose={() => setModal(null)} onAdded={(item) => { setModal(null); add(item); refreshItemCache().catch(() => {}); }} />
      )}
      {modal?.kind === 'new' && (
        <NewProduct code={modal.code} onClose={() => setModal(null)} onAdded={async () => { setModal(null); await refreshItemCache().catch(() => {}); const it = findCachedByCode(modal.code); if (it) add(it); }} />
      )}
      {modal?.kind === 'done' && (
        <Modal title={modal.offline ? 'Sale saved offline' : 'Sale complete'} onClose={() => setModal(null)} footer={<>{modal.saleId && <button onClick={async () => { if (!(await printReceipt(modal.saleId!, session.user.name))) setError(new Error('The browser blocked the print window. Allow pop-ups for this site.')); }}>Print receipt</button>}<button className="primary big" autoFocus onClick={() => setModal(null)}>New sale</button></>}>
          <div className="totals" style={{ padding: 0 }}>
            <div className="line"><span>Total</span><span>{usd(modal.total)}</span></div>
            {modal.change > 0 && <div className="grand"><span>Change due</span><span>{usd(modal.change)}</span></div>}
          </div>
          {modal.offline && <p className="muted">It will be sent automatically when the connection is back.</p>}
        </Modal>
      )}
    </div>
  );
}

function CashModal({ total, rounding, onClose, onTender }: { total: number; rounding: number; onClose: () => void; onTender: (cents: number) => void }) {
  const due = total + rounding;
  const [typed, setTyped] = useState('');
  const quick = [...new Set([due, Math.ceil(due / 500) * 500, Math.ceil(due / 1000) * 1000, Math.ceil(due / 2000) * 2000, 5000, 10000].filter((x) => x >= due))].sort((a, b) => a - b).slice(0, 6);
  const typedCents = Math.round(Number(typed || '0') * 100);
  return (
    <Modal title="Cash" onClose={onClose} footer={<button className="primary big" disabled={typedCents < due} onClick={() => onTender(typedCents)}>Take {usd(typedCents)}</button>}>
      <div className="totals" style={{ padding: 0 }}>
        {rounding !== 0 && <div className="line"><span>Rounded to the nearest 5¢ (no pennies)</span><span>{rounding > 0 ? '+' : '−'}{Math.abs(rounding)}¢</span></div>}
        <div className="grand"><span>Due</span><span>{usd(due)}</span></div>
      </div>
      <div className="cash-keys">
        {quick.map((q) => <button key={q} onClick={() => onTender(q)}>{q === due ? 'Exact' : usd(q).replace('.00', '')}</button>)}
      </div>
      <label>Other amount<input className="num-input" inputMode="decimal" value={typed} onChange={(e) => setTyped(e.target.value.replace(/[^\d.]/g, ''))} placeholder="0.00" /></label>
    </Modal>
  );
}

function AgeModal({ timezone, onClose, onPass }: { timezone?: string; onClose: () => void; onPass: (a: AgeResult) => void }) {
  const [scan, setScan] = useState('');
  const [result, setResult] = useState<{ passed: boolean; reason?: string; idExpired: boolean } | null>(null);
  const today = todayIn(timezone ?? 'America/New_York');
  const check = (raw: string) => {
    const r = ageCheck(parseAamva(raw), today);
    setResult(r);
    if (r.passed) setTimeout(() => onPass({ method: 'id_scan', passed: true, idExpired: false }), 400);
  };
  return (
    <Modal title="Check ID" onClose={onClose} footer={<>
      <button onClick={onClose}>Cancel sale</button>
      <button className="primary" onClick={() => onPass({ method: 'visual', passed: true })}>Checked by eye — customer is 21+</button>
    </>}>
      <p className="muted">Scan the barcode on the back of the licence. The date of birth stays on this register; only pass or fail is recorded.</p>
      <textarea autoFocus rows={3} value={scan} aria-label="Licence barcode"
        onChange={(e) => setScan(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && e.ctrlKey) check(scan); }}
        onBlur={() => scan && check(scan)} placeholder="Waiting for licence scan…" />
      {scan && !result && <button onClick={() => check(scan)}>Check this scan</button>}
      {result && (result.passed
        ? <div className="notice good">ID passes.</div>
        : <div className="notice bad">Do not sell: {result.reason}.</div>)}
    </Modal>
  );
}

function AddFromCatalog({ product, onClose, onAdded }: { product: any; onClose: () => void; onAdded: (i: CachedItem) => void }) {
  const [price, setPrice] = useState('');
  const [err, setErr] = useState<unknown>(null);
  return (
    <Modal title="Known product, new to this store" onClose={onClose} footer={<button className="primary" disabled={!price} onClick={async () => {
      try {
        const it = await api<any>('/api/items', { body: { productId: product.id, retailCents: Math.round(Number(price) * 100) } });
        onAdded({ ...it, brand: product.brand, name: product.name, size_ml: product.size_ml, category: product.category, on_hand: 0, codes: [] });
      } catch (e) { setErr(e); }
    }}>Add and ring up</button>}>
      <p><b>{product.brand} {product.name}</b> · {sizeLabel(product.size_ml)} {product.status === 'verified' ? <span className="chip good">Verified</span> : <span className="chip warn">Unverified</span>}</p>
      <p className="muted">Found in the shared catalog. Set your shelf price; it stays private to your store.</p>
      <ErrorNote error={err} />
      <label>Shelf price<input className="num-input" autoFocus inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value.replace(/[^\d.]/g, ''))} placeholder="0.00" /></label>
      {err instanceof ApiError && err.status === 403 && <p className="faint">A manager has to add new items.</p>}
    </Modal>
  );
}

function NewProduct({ code, onClose, onAdded }: { code: string; onClose: () => void; onAdded: () => void }) {
  const [f, setF] = useState({ brand: '', name: '', size: '750ml', category: 'spirits', casePack: '12', price: '' });
  const [err, setErr] = useState<unknown>(null);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setF({ ...f, [k]: e.target.value });
  const ok = f.brand && f.name && f.size && f.price;
  return (
    <Modal title="New product" onClose={onClose} footer={<button className="primary" disabled={!ok} onClick={async () => {
      try {
        await api('/api/catalog/products', { body: { code, brand: f.brand, name: f.name, size: f.size, category: f.category, casePack: Number(f.casePack) || 12, retailCents: Math.round(Number(f.price) * 100) } });
        onAdded();
      } catch (e) { setErr(e); }
    }}>Save and ring up</button>}>
      <p className="muted">Barcode <span className="num">{code}</span> isn’t in the shared catalog yet. What you enter here helps every store that scans it next.</p>
      <ErrorNote error={err} />
      <div className="field-row">
        <label>Brand<input autoFocus value={f.brand} onChange={set('brand')} /></label>
        <label>Product<input value={f.name} onChange={set('name')} placeholder="e.g. Reposado Tequila" /></label>
      </div>
      <div className="field-row">
        <label>Size<input value={f.size} onChange={set('size')} placeholder="750ml, 1.75L, pint" /></label>
        <label>Type<select value={f.category} onChange={set('category')}>
          {['spirits', 'wine', 'beer', 'rtd', 'cider', 'non_alcoholic', 'accessory'].map((c) => <option key={c} value={c}>{c.replace('_', ' ')}</option>)}
        </select></label>
        <label>Per case<input inputMode="numeric" value={f.casePack} onChange={set('casePack')} /></label>
        <label>Shelf price<input className="num-input" inputMode="decimal" value={f.price} onChange={set('price')} placeholder="0.00" /></label>
      </div>
    </Modal>
  );
}
